import { createContext, type ReactNode, useContext, useState } from "react";

const DEFAULT_PAGE_SIZE = 10;

export type PaginationContextType = {
	pageIndex: number;
	setPageIndex: (index: number | ((prev: number) => number)) => void;
	pageSize: number;
	setPageSize: (size: number | ((prev: number) => number)) => void;
	resetPagination: () => void;
};

const PaginationContext = createContext<PaginationContextType | undefined>(
	undefined,
);

interface PaginationProviderProps {
	children: ReactNode;
	defaultPageSize?: number;
	defaultPageIndex?: number;
}

export function PaginationProvider({
	children,
	defaultPageSize = DEFAULT_PAGE_SIZE,
	defaultPageIndex = 0,
}: PaginationProviderProps) {
	const [pageIndex, setPageIndex] = useState(defaultPageIndex);
	const [pageSize, setPageSize] = useState(defaultPageSize);

	const resetPagination = () => {
		setPageIndex(defaultPageIndex);
		setPageSize(defaultPageSize);
	};

	return (
		<PaginationContext.Provider
			value={{
				pageIndex,
				setPageIndex,
				pageSize,
				setPageSize,
				resetPagination,
			}}
		>
			{children}
		</PaginationContext.Provider>
	);
}

export function usePaginationContext() {
	const context = useContext(PaginationContext);
	if (context === undefined) {
		throw new Error("usePagination must be used within a PaginationProvider");
	}
	return context;
}
